// Catalog.ID Client — Mail-Request Mode Handlers
// ============================================================================
// Per-mode dispatch for mail-originated `registry-request` envelopes. The
// dispatcher in `client.ts` routes here based on
// `request.requesterId === 'mail'`.
//
// A SEPARATE DISPATCHER from chat's, deliberately. Both families' modes carry
// identical field tuples, and both reach into the keybag for an account-level
// signing root. The mode literal, the family boundary here, and the cert type
// string are the three things keeping a chat-signed envelope from making the
// MAIL root sign — which is the whole reason mail has its own root
// (migration §5.2).
//
// Inbound envelopes have already been signature-verified by the cld-id server's
// relay, and BOTH mail modes are pinned in `MandatoryVerifiedInboundModes`, so
// an envelope whose canonical bytes could not be built never reaches this file
// — it is refused upstream with `canonical-unavailable`. Browser code
// re-verifies only the body↔canonical binding: the signature covers a digest,
// the body carries the material, so a relay-verified envelope cannot be paired
// with a substituted sub-key or a substituted address record.
//
// v1 modes:
//   - `issue-mail-cert`: mail_sign sub-key certification — see
//     `mail-cert-handlers.ts`. Ceremony-grade, so it WAITS for an unlock; NO
//     consent modal, because unlock IS the ceremony and a per-renewal prompt
//     would train click-through.
//   - `create-mail-address`: create the `mail_address` claim — see
//     `mail-address-handlers.ts`. Ceremony-grade AND consent-gated, because it
//     is the mail mode that writes durable account state naming a public
//     identifier. It does not publish and does not register with the directory.
//   - `export-mail-encrypt`: hand the module the account's `mail_encrypt`
//     private key(s), wrapped to its ephemeral wrap key — see
//     `mail-key-export-handlers.ts`. Ceremony-grade AND consent-gated: an
//     account private key leaves the keybag for a machine (migration §5.1).
// ============================================================================

import { handleIssueMailCert } from './mail-cert-handlers';
import { handleCreateMailAddress } from './mail-address-handlers';
import { handleExportMailEncrypt } from './mail-key-export-handlers';
import type { MailRequest, MailResponse, MailDispatchDeps } from './mail-types';

type ResponseBase = Pick<MailResponse, 'type' | 'to' | 'requestId'>;

/** Re-exported from `mail-types` (where it lives to keep mail-handlers ↔
 *  mail-cert-handlers acyclic) so importers have one obvious home. */
export type { MailDispatchDeps };

export async function dispatchMailRequest(
    request: MailRequest,
    deps: MailDispatchDeps,
): Promise<MailResponse> {
    const base: ResponseBase = {
        type: 'registry-response',
        to: request.requesterId,
        requestId: request.requestId,
    };

    if (!request._senderServiceId || request._senderServiceId !== request.requesterId) {
        return { ...base, status: 'unavailable', reason: 'sender-mismatch' };
    }
    if (request.requesterId !== 'mail') {
        return { ...base, status: 'unavailable', reason: 'unsupported-requester' };
    }

    const viewer = deps.getActiveCid();
    if (!viewer) return { ...base, status: 'unavailable', reason: 'no-active-identity' };

    switch (request.mode) {
        case 'issue-mail-cert':
            return await handleIssueMailCert(request, base, viewer, deps);
        case 'create-mail-address':
            return await handleCreateMailAddress(request, base, viewer, deps);
        case 'export-mail-encrypt':
            return await handleExportMailEncrypt(request, base, viewer, deps);
        default:
            return { ...base, status: 'unavailable', reason: 'unsupported-mode' };
    }
}
